import { useFormikContext } from 'formik';
import React, { Fragment } from 'react';
import { BucketFormikInput as Field } from 'Src/types/forms';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { t } from 'Src/i18n';
import type {
  FormFieldOption,
  NewsletterOptins,
} from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useAppContext } from 'Context/ReactAppContext';
import type { FormCivilities, FormValues } from './PrebookingForm.utils';
import { ADULTS, KIDS } from './PrebookingForm.constants';
import { PrebookingFormCompanion } from './PrebookingFormCompanion';
export type PrebookingFormTravelInformationsProps = {
  agencies: Array<FormFieldOption>;
  civilities: FormCivilities;
  dateFormat: string;
  hasUnicodeFields: boolean;
  hasUser: boolean;
  consentOptins: NewsletterOptins;
};
export const PrebookingFormTravelInformations = ({
  agencies,
  civilities,
  dateFormat,
  hasUnicodeFields,
  hasUser,
  consentOptins,
}: PrebookingFormTravelInformationsProps) => {
  const { values, initialValues } = useFormikContext<FormValues>();
  const appContext = useAppContext();
  const hasLoggedUserBirthdate = hasUser && !!initialValues.traveler.birthdate;
  const hasLoggedUserPhone = hasUser && !!initialValues.traveler.phone;
  const { adults, adultsNumber, kids, kidsNumber } = values;
  const { optinEmail, optinPhone, optinSms, optinProfile } = consentOptins;
  const adultsInt = parseInt(adultsNumber, 10);
  const kidsInt = parseInt(kidsNumber, 10);
  const hasAddtionnalTravelers = adultsInt > 1 || kidsInt > 0;
  const showUnicodeFieldsFirst = isFeatureFlipped(appContext)('prebooking.unicodeFieldsFirst');
  return (
    <div id="pbf-step-2" data-testid="prebooking-form-form-travel-informations">
      <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4" id="pbf-step-2-title">
        {t('prebookingForm.mainTraveler.title')}
      </Title>
      <p className="text-12all m-0 mb-10 px-10">{t('prebookingForm.mandatoryFieldsInformation')}</p>
      <div className="flex flex-wrap">
        <div className="w-full">
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="traveler-civility-field"
              hideErrorMessage
              isDisabled={hasUser}
              isRequired
              label={t('prebookingForm.mainTraveler.civilityPlaceholder')}
              name="traveler.civility"
              options={civilities.adults}
              placeholder={t('prebookingForm.mainTraveler.civilityPlaceholder')}
              type="select"
            />
          </div>
        </div>
        {hasUnicodeFields && showUnicodeFieldsFirst && (
          <Fragment>
            <div className="w-full p-10 md:w-1/2">
              <Field
                dataTestId="traveler-firstNameUnicode-field"
                hideErrorMessage
                isDisabled={hasUser}
                isRequired
                label={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
                name="traveler.firstNameUnicode"
                placeholder={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
                type="text"
              />
            </div>
            <div className="w-full p-10 md:w-1/2">
              <Field
                dataTestId="traveler-lastNameUnicode-field"
                hideErrorMessage
                isDisabled={hasUser}
                isRequired
                label={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
                name="traveler.lastNameUnicode"
                placeholder={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
                type="text"
              />
            </div>
          </Fragment>
        )}
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-firstName-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.firstNamePlaceholder')}
            name="traveler.firstName"
            placeholder={t('prebookingForm.mainTraveler.firstNamePlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-lastName-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.lastNamePlaceholder')}
            name="traveler.lastName"
            placeholder={t('prebookingForm.mainTraveler.lastNamePlaceholder')}
            type="text"
          />
        </div>
        {hasUnicodeFields && !showUnicodeFieldsFirst && (
          <Fragment>
            <div className="w-full p-10 md:w-1/2">
              <Field
                dataTestId="traveler-firstNameUnicode-field"
                hideErrorMessage
                isDisabled={hasUser}
                isRequired
                label={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
                name="traveler.firstNameUnicode"
                placeholder={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
                type="text"
              />
            </div>
            <div className="w-full p-10 md:w-1/2">
              <Field
                dataTestId="traveler-lastNameUnicode-field"
                hideErrorMessage
                isDisabled={hasUser}
                isRequired
                label={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
                name="traveler.lastNameUnicode"
                placeholder={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
                type="text"
              />
            </div>
          </Fragment>
        )}
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-birthdate-field"
            hideErrorMessage
            isDisabled={hasLoggedUserBirthdate}
            isRequired
            label={t('prebookingForm.mainTraveler.birthDateInformation')}
            name="traveler.birthdate"
            placeholder={t('prebookingForm.mainTraveler.birthDateInformation')}
            type="birthdate"
            dayPlaceholder="DD"
            monthPlaceholder="MM"
            yearPlaceholder="YYYY"
            dateFormat={dateFormat}
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-gm-number-field"
            hideErrorMessage
            isDisabled={hasUser}
            label={t('prebookingForm.mainTraveler.gmNumberPlaceholder')}
            name="traveler.memberNumber"
            placeholder={t('prebookingForm.mainTraveler.gmNumberPlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-email-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.emailPlaceholder')}
            name="traveler.email"
            placeholder={t('prebookingForm.mainTraveler.emailPlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full">
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="traveler-phone-field"
              hideErrorMessage
              isDisabled={hasLoggedUserPhone}
              isRequired
              label={t('prebookingForm.mainTraveler.phoneNumberPlaceholder')}
              name="traveler.phone"
              placeholder={t('prebookingForm.mainTraveler.phoneNumberPlaceholder')}
              type="text"
            />
          </div>
        </div>
        {agencies.length > 1 && (
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="traveler-agency-field"
              hideErrorMessage
              label={t('prebookingForm.mainTraveler.favoriteTravelAgencyLabel')}
              name="agency"
              options={agencies}
              placeholder={t('prebookingForm.mainTraveler.favoriteTravelAgencyPlaceholder')}
              type="select"
            />
          </div>
        )}
        {hasAddtionnalTravelers && (
          <div className="w-full p-10">
            {adults.map((_adult, index) => (
              <PrebookingFormCompanion
                key={`adult-${index}`}
                companionGroup={ADULTS}
                dateFormat={dateFormat}
                civilities={civilities.adults}
                hasUnicodeFields={hasUnicodeFields}
                index={index}
              />
            ))}
            {kids.map((kid, index) => (
              <PrebookingFormCompanion
                key={`kid-${index}`}
                companionGroup={KIDS}
                dateFormat={dateFormat}
                civilities={civilities.kids}
                hasUnicodeFields={hasUnicodeFields}
                index={index}
                birthday={kid.birthdate}
              />
            ))}
          </div>
        )}
        <div className="w-full p-10">
          <p className="mb-10 w-full text-14 font-bold">
            {t('prebookingForm.mainTraveler.optinCommunicationsInformation')}
          </p>
          {optinEmail && (
            <Field
              className="mb-10"
              dataTestId="optin-email-field"
              label={optinEmail.title}
              name="optinEmail"
              optimized
              type="checkbox"
            />
          )}
          {optinPhone && (
            <Field
              className="mb-10"
              dataTestId="optin-phone-field"
              label={optinPhone.title}
              name="optinPhone"
              optimized
              type="checkbox"
            />
          )}
          {optinSms && (
            <Field
              className="mb-10"
              dataTestId="optin-sms-field"
              label={optinSms.title}
              name="optinSms"
              optimized
              type="checkbox"
            />
          )}
          {optinEmail && optinEmail.description && (
            <Text className="mt-15" theme="text-sans-n2" text={optinEmail.description} />
          )}
          {optinProfile && optinProfile.description && (
            <Text className="mt-15" theme="text-sans-n2" text={optinProfile.description} />
          )}
        </div>
      </div>
    </div>
  );
};
